import React, { useCallback, useEffect, useState } from 'react'
import { Box } from '@mui/material'
import DefaultBeforeSelectText from '../../common/DefaultBeforeSelectText'
import JobStatusChangeDialog from '../PlannedJobs/components/JobStatusChangeDialog'
import ReportQuantityDialog from '../ReportQuantityDialog'
import CancelPartsDialog from '../CancelPartsDialog'
import AddPartsDialog from '../AddPartsDialog'
import AlertUI from '../../../../generic/AlertUI'
import { ActiveSingleJob } from '../ActiveSingleJob'
import { useActiveMultiJob } from './hooks/useActiveMultiJob'
import { apiGet, apiPost } from '../../../../generic/Api_Functions'
import ActiveJobList from './components/ActiveJobList'
import useFrontEndConfig from '../../../../generic/hooks/useFrontEndConfig'
import { getJobAutoReportParts, getJobAutoReportResources, getJobBatchId, getJobExternalCircuit, getJobGovernedByAcceptance, getJobManagePreparation, getJobStatusCode, isJobInPreparation, hasPendingRawMaterialResources, resolveReportOutcome, shouldOpenInstanceSelectionModal } from '../../common/reportOutcome'
import { resolveJobModeActions } from '../../common/jobModeActions'
import useJobModeTransition from '../../common/useJobModeTransition'
import JobModeChangeDialog from '../../common/JobModeChangeDialog'
import { useBatchActions } from '../../common/batch/useBatchActions'
import BatchBlockedNotice from '../../common/batch/BatchBlockedNotice'
import BatchFinishDialog from '../../common/batch/BatchFinishDialog'
import BatchActionsBar from './components/BatchActionsBar'
import { buildBatchBadges } from '../../common/batch/batchLabel'
import { resolveBatchWarningKind, BATCH_WARNING_KIND } from '../../common/batch/batchWarning'
import BatchWarningDialog from '../../common/batch/BatchWarningDialog'
import { JOB_ACTION_INTENT } from '../PlannedJobs/utils/jobStatus'

/**
 * Container for the active multi-job widget.
 *
 * Manages:
 * - Dialog states (change status, report quantity)
 * - Business logic for reporting quantities and changing statuses
 * - Orchestration between job list and detail (ActiveSingleJob)
 */
const ActiveMultiJobContainer = () => {
  const {
    t_,
    jobProgress,
    activeItems,
    fabricationOrder,
    jobReference,
    node,
    reloadProgress,
    refreshActiveJobs,
    materials,
    device,
    worker,
    selectNode,
    unSelectNode,
    listProgress,
    plannedEffective,
    refreshActiveJobsProgress,
    machineStorage
  } = useActiveMultiJob()

  const [frontEndConfig] = useFrontEndConfig()

  const [changeStatusDialog, setChangeStatusDialog] = useState({ open: false })
  // S124: the two-action mode modal behind "Change status".
  const [modeChangeOpen, setModeChangeOpen] = useState(false)
  const [reportQuantityDialog, setReportQuantityDialog] = useState({ open: false, hideOkOption: false })
  const [cancelPartsDialog, setCancelPartsDialog] = useState({ open: false })
  const [addPartsDialog, setAddPartsDialog] = useState({ open: false })
  const [qualityTypes, setQualityTypes] = useState([])

  // Supervisor capability gates the cancel/add part actions. When no worker is logged in we still
  // show the actions so WorkerRequiredButton can prompt the presence flow (S31).
  const showPartActions = worker ? worker.supervisor === true : true
  const [counter, setCounter] = useState(0)
  const [AlertElement, showAlert] = AlertUI()

  // When INPUT_GREATER_AMOUNT_THAN_THE_OF is enabled (or not yet loaded), the first job may report
  // beyond planned to replace scrapped units. When disabled, every job is capped to "remaining".
  const allowGreaterThanFo = frontEndConfig?.inputGreaterAmountThanTheFo !== false
  const isFirstJob = jobProgress?.isFirst ?? false
  const maxReportable = isFirstJob && allowGreaterThanFo
    ? Infinity
    : isFirstJob
      ? Math.max(jobProgress?.remaining ?? 0, 0)
      : Math.min(jobProgress?.available ?? 0, jobProgress?.remaining ?? 0)

  const handleIncrement = () => setCounter((prev) => Math.min(prev + 1, maxReportable))
  const handleDecrement = () => setCounter((prev) => (prev > 0 ? prev - 1 : 0))
  const handleSetCounter = (value) => setCounter(Math.min(Math.max(value, 0), maxReportable))

  useEffect(() => {
    setCounter(0)
  }, [jobReference?.id, fabricationOrder?.id])

  // Re-clamp the counter if the reportable limit decreases (progress refreshes every 5s)
  useEffect(() => {
    setCounter((prev) => Math.min(prev, maxReportable))
  }, [maxReportable])

  useEffect(() => {
    let mounted = true
    apiGet('qualityTypesAll')
      .then(types => {
        if (mounted && Array.isArray(types)) {
          setQualityTypes(types)
        }
      })
      .catch(err => console.error('Error fetching quality types', err))
    return () => { mounted = false }
  }, [])

  const handleChangeStatus = (status) => {
    setChangeStatusDialog({ open: true, jobReference, fabricationOrder, statusCode: status, node })
  }

  // S16/S37/S39/S19: posts the OK report and resolves the outcome. `instanceSelections` is only
  // populated when the SELECCION modal ran first (S16, added to the single report line — fleco 1,
  // "one modal per batch"); it is omitted otherwise so the request body is unchanged (CLAUDE.md:
  // no null/undefined placeholders in business logic).
  // Story 79 / S33: `generateMissingMaterial` travels the same way — the operator marked "create
  // required material" in that modal, and Sun derives the missing amount for the whole report.
  const submitOkReport = async (qty, instanceSelections = [], generateMissingMaterial = false) => {
    try {
      const okType = qualityTypes.find(t => t.code === 'Ok')

      if (okType && worker && device) {
        const quantityReports = [{
          comment: '',
          qualityType: okType.id,
          quantity: Number(qty),
          ...(instanceSelections.length > 0 ? { instanceSelections } : {}),
          ...(generateMissingMaterial ? { generateMissingMaterial: true } : {})
        }]
        const result = await apiPost('mos/fabricationOrderReportQuantity/' + worker.id + '/' + device.id + '/' + fabricationOrder.id + '/' + jobReference.id, quantityReports)
        reloadProgress()
        refreshActiveJobsProgress()
        // S37/S39/S19: the endpoint now returns {solicitadas, reportadas, falloEn, motivo} instead
        // of a fixed message — resolveReportOutcome turns it into the alert to show and, on a
        // partial report, the pending quantity so a retry only resends what is missing.
        const outcome = resolveReportOutcome({
          result,
          requestedQuantity: Number(qty),
          autoReportResources: getJobAutoReportResources(activeItems, fabricationOrder, jobReference),
          hasPendingResources: hasPendingRawMaterialResources(materials, node),
          t_
        })
        showAlert({ severity: outcome.severity, message: outcome.message })
        setCounter(outcome.pending)
      } else {
        setReportQuantityDialog({
          open: true,
          node,
          jobReference,
          fabricationOrder,
          statusCode: 'IN_PROGRESS',
          initialQuantity: qty
        })
      }
    } catch (error) {
      console.error(error)
      // Surface the backend message verbatim (e.g. "Cannot report N part(s) as OK: only M have
      // their required raw material fully consumed at this node") instead of a generic text.
      showAlert({ severity: 'error', message: t_(error.message || 'Error reporting quantity') })
    }
  }

  const handleReportOk = async (qty) => {
    if (qty <= 0) {
      showAlert({ severity: 'warning', message: t_('Report quantity cannot be empty') })
      return
    }
    if (jobProgress && (!isFirstJob || !allowGreaterThanFo)) {
      if (qty > maxReportable) {
        showAlert({ severity: 'warning', message: t_('Report quantity cannot be greater than remaining') })
        return
      }
    }

    // S16 (GATED — pending follow-up): SELECCION instance selection on report needs a per-piece,
    // multi-reference contract with Sun that is not closed yet (see the story's
    // dilema-seleccion-consumo-por-pieza.md). Until then we neither open the incomplete modal nor
    // post an ambiguous report: we tell the operator to consume from the Components widget. The
    // detector stays so re-enabling is a one-line swap.
    const autoReportResources = getJobAutoReportResources(activeItems, fabricationOrder, jobReference)
    if (shouldOpenInstanceSelectionModal(machineStorage, autoReportResources)) {
      showAlert({ severity: 'warning', message: t_('In Selection mode, consume the material from the Components widget') })
      return
    }

    await submitOkReport(qty)
  }

  const handleReportNok = (qty) => {
    if (qty <= 0) {
      showAlert({ severity: 'warning', message: t_('Report quantity cannot be empty') })
      return
    }
    if (jobProgress && (!isFirstJob || !allowGreaterThanFo)) {
      if (qty > maxReportable) {
        showAlert({ severity: 'warning', message: t_('Report quantity cannot be greater than remaining') })
        return
      }
    }
    setReportQuantityDialog({
      open: true,
      node,
      jobReference,
      fabricationOrder,
      statusCode: 'IN_PROGRESS',
      initialQuantity: qty,
      hideOkOption: true
    })
  }

  const handleStatusChangeSuccess = useCallback(() => {
    unSelectNode()
    setTimeout(() => refreshActiveJobs(), 500)
  }, [unSelectNode, refreshActiveJobs])

  const handleReportQuantitySuccess = useCallback((outcome) => {
    setReportQuantityDialog(prev => ({ ...prev, open: false }))
    reloadProgress()
    refreshActiveJobsProgress()
    if (outcome) {
      showAlert({ severity: outcome.severity, message: outcome.message })
      setCounter(outcome.pending)
    } else {
      setCounter(0)
    }
  }, [reloadProgress, refreshActiveJobsProgress, showAlert])

  const handlePartActionSuccess = useCallback(() => {
    reloadProgress()
    refreshActiveJobsProgress()
    refreshActiveJobs()
  }, [reloadProgress, refreshActiveJobsProgress, refreshActiveJobs])

  // ⚠️ EVERY HOOK MUST STAY ABOVE THE EARLY RETURN BELOW. This container renders the empty branch
  // first -- the widget mounts before the first active-context poll answers -- so a hook placed after
  // it is skipped on that render and registered on the next, which is exactly the "Rendered more
  // hooks than during the previous render" crash story 113 shipped and
  // `__tests__/ActiveMultiJobContainerHookOrder.test.jsx` now pins. Story 120 (S151) added
  // `useBatchActions` to this same list, for the exact same reason: it must run on the empty-branch
  // render too, or the very next render (jobs arrive) would register it late and crash again. S141
  // (P9) adds `finishBatchWarningOpen` right below, for the identical reason.
  const { goToExecution, backToPreparation, busy: modeTransitionBusy } = useJobModeTransition({
    device,
    fabricationOrder,
    jobReference,
    worker,
    activeJobs: activeItems,
    refreshActiveJobs,
    showAlert,
    t_
  })

  const {
    batchGroups,
    selectedBatchGroup,
    pauseBatchGroup,
    blockedPause,
    closeBlockedPause,
    finishBatchId,
    openFinishBatch,
    closeFinishBatch,
    handleBatchFinished
  } = useBatchActions({
    activeItems,
    device,
    worker,
    // ENMIENDA 06 (G3-10): el job seleccionado del maestro-detalle es el que este contexto publica en
    // `fabricationOrder`/`jobReference` -- de el, y solo de el, dependen ahora las acciones de lote.
    selectedJob: { fabricationOrderId: fabricationOrder?.id, jobReferenceId: jobReference?.id },
    refreshActiveJobs,
    showAlert,
    t_
  })

  // S141 (third surface — the station widgets, closed by P9): the SAME resolver/dialog P6 already
  // wired for the row-shaped surfaces (Planned Jobs tabs, All Active Jobs) — a plain useState, so it
  // must stay above the early return below, same as every other hook in this container.
  const [finishBatchWarningOpen, setFinishBatchWarningOpen] = useState(false)

  if (!activeItems || activeItems.length === 0) {
    return <DefaultBeforeSelectText />
  }

  // When the job's technology auto-reports parts, clicking Finish makes Sun report the remaining
  // quantity automatically and close the job, so Finish must not be blocked at all — neither by the
  // pending quantity nor by canFinish (which stays false precisely because those parts are not yet
  // reported; requiring it here would deadlock the auto-report flow).
  const autoReportParts = getJobAutoReportParts(activeItems, fabricationOrder, jobReference)
  // S51 (CA11/RN24/D18, story 86): a job governed by the acceptance form is observable but not
  // operable from this widget either — Pause and Finish both come out disabled, on top of (not
  // instead of) the gate above. Computed independently from ActiveSingleJobContainer (the two
  // containers do not share this calculation).
  const governedByAcceptance = getJobGovernedByAcceptance(activeItems, fabricationOrder, jobReference)
  // S79 (story 100, RN34/C9(1)): a JOB that crossed the external circuit gate is a DIFFERENT domain
  // concept, computed independently here too (own per-job lookup) — gates the SAME two controls as
  // above, PLUS Report, which #86 never touched for the MOD job.
  const externalCircuitJob = getJobExternalCircuit(activeItems, fabricationOrder, jobReference)
  const pauseDisabled = governedByAcceptance || externalCircuitJob
  // Story 113 (S134/S139): same two terms and same precedence as the single-job widget.
  const inPreparation = isJobInPreparation(activeItems, fabricationOrder, jobReference) &&
    getJobManagePreparation(activeItems, fabricationOrder, jobReference)
  const reportDisabled = externalCircuitJob || inPreparation
  const externalCircuitJobTooltip = externalCircuitJob
    ? t_('External job: actions are governed by the external jobs widget')
    : ''
  // S62 (delta signed 2026-08-04): same explanation as every other MOS surface, and same reason for it to be
  // its own value rather than derived from finishDisabled, which is composite. Computed independently here,
  // like the gate itself.
  const governedByAcceptanceTooltip = governedByAcceptance
    ? t_('Acceptance job: actions are governed by the acceptance form')
    : externalCircuitJobTooltip
  const reportDisabledTooltip = reportDisabled
    ? (governedByAcceptanceTooltip || t_('Reporting is only available in execution'))
    : ''
  // Story 113 (S120/S133, R-G): the layout comes from the single shared point -- this widget used to
  // recompute the same formula as the single-job one, which is exactly the drift R-G is about.
  const finishDisabled = governedByAcceptance || externalCircuitJob ||
    (!autoReportParts && ((jobProgress?.remaining ?? 0) > 0 || !jobProgress?.canFinish))
  const modeActions = resolveJobModeActions({
    managePreparation: getJobManagePreparation(activeItems, fabricationOrder, jobReference),
    statusCode: getJobStatusCode(activeItems, fabricationOrder, jobReference),
    finishDisabled,
    pauseDisabled,
    gatedTooltip: governedByAcceptanceTooltip,
    modeTransitionBusy
  })
  const handleBackToPreparation = () => {
    setModeChangeOpen(false)
    backToPreparation()
  }
  const handlePauseFromModeDialog = () => {
    setModeChangeOpen(false)
    handleChangeStatus('INTERRUPTED')
  }

  // S141 (third surface, R-16): the SAME resolver `PlannedJobListWidget.jsx` (P6) already consumes
  // for the row-shaped surfaces. `getJobBatchId` reads the current selection's `batchId` straight off
  // the active-context item (F3, no new call) and `resolveBatchWarningKind` decides synchronously --
  // no `batchId` (an older Sun, or simply no batch, RN69/POL7) resolves to NONE, so Finish behaves
  // exactly as it did before this story (S139/S142): this is UX prevention only (RN71.2), Sun never
  // rejects the individual close of a batch member.
  const currentBatchId = getJobBatchId(activeItems, fabricationOrder, jobReference)
  const finishWarningKind = resolveBatchWarningKind({ batchId: currentBatchId }, JOB_ACTION_INTENT.FINISH)

  const handleFinishClick = () => {
    if (finishWarningKind === BATCH_WARNING_KIND.FINISH_BATCH_MEMBER) {
      setFinishBatchWarningOpen(true)
      return
    }
    handleChangeStatus('FINISHED')
  }
  const handleFinishWarningClose = () => setFinishBatchWarningOpen(false)
  // "Finish only this job" -- today's individual close, with ALL its rules and ALL its rejections
  // (the exact same path S131/S137 already use for the row-shaped surfaces).
  const handleFinishWarningChooseIndividual = () => {
    setFinishBatchWarningOpen(false)
    handleChangeStatus('FINISHED')
  }
  // "Finish the whole batch" -- reuses the N4+N5 dialog this container already mounts (P8, via
  // `useBatchActions`/P7), never a second one.
  const handleFinishWarningChooseBatch = () => {
    setFinishBatchWarningOpen(false)
    openFinishBatch(currentBatchId)
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: (theme) => theme.zIndex.snackbar }}>
        {AlertElement}
      </Box>
      {/* S124-S126: exactly two actions behind "Change status". */}
      <JobModeChangeDialog
        open={modeChangeOpen}
        onClose={() => setModeChangeOpen(false)}
        onBackToPreparation={handleBackToPreparation}
        onPauseWork={handlePauseFromModeDialog}
      />
      <JobStatusChangeDialog
        open={changeStatusDialog.open}
        onCancell={() => setChangeStatusDialog(prev => ({ ...prev, open: false }))}
        onSuccess={handleStatusChangeSuccess}
        node={changeStatusDialog.node}
        jobReference={changeStatusDialog.jobReference}
        fabricationOrder={changeStatusDialog.fabricationOrder}
        statusCode={changeStatusDialog.statusCode}
      />
      <ReportQuantityDialog
        open={reportQuantityDialog.open}
        onCancel={() => setReportQuantityDialog(prev => ({ ...prev, open: false }))}
        onSuccess={handleReportQuantitySuccess}
        jobReference={jobReference}
        fabricationOrder={fabricationOrder}
        statusCode={reportQuantityDialog.statusCode}
        quantity={reportQuantityDialog.initialQuantity}
        hideOkOption={reportQuantityDialog.hideOkOption}
        materials={materials}
      />
      <CancelPartsDialog
        open={cancelPartsDialog.open}
        onCancel={() => setCancelPartsDialog({ open: false })}
        onSuccess={handlePartActionSuccess}
        fabricationOrder={fabricationOrder}
        jobReference={jobReference}
        maxQuantity={jobProgress?.remaining ?? 0}
      />
      <AddPartsDialog
        open={addPartsDialog.open}
        onCancel={() => setAddPartsDialog({ open: false })}
        onSuccess={handlePartActionSuccess}
        fabricationOrder={fabricationOrder}
        jobReference={jobReference}
      />

      {/* S147: the SAME renderer N5's BLOCKED already uses (D-SP14) -- a 200 that writes nothing, so
          none of the active jobs change on screen and the operator can retry with nothing to clean
          up first. */}
      <BatchBlockedNotice
        open={blockedPause.open}
        jobs={blockedPause.jobs}
        onClose={closeBlockedPause}
      />
      {/* "Finish batch" opens the already-built N4+N5 dialog (paquete P8) untouched -- this container
          only decides WHICH batchId to open it with. */}
      <BatchFinishDialog
        open={Boolean(finishBatchId)}
        deviceId={device?.id}
        workerId={worker?.id}
        batchId={finishBatchId}
        onClose={closeFinishBatch}
        onFinished={handleBatchFinished}
      />
      {/* S141 (third surface): the ONE resolver/dialog (P6) also gates THIS Finish -- "only this job"
          runs today's individual close untouched; "the whole batch" opens the SAME BatchFinishDialog
          above, never a second one. */}
      <BatchWarningDialog
        open={finishBatchWarningOpen}
        kind={finishWarningKind}
        onClose={handleFinishWarningClose}
        onChooseIndividual={handleFinishWarningChooseIndividual}
        onChooseBatch={handleFinishWarningChooseBatch}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '140px minmax(0, 1fr)' },
          gap: 1,
          // S246: el área de trabajo ocupa lo que queda; la pareja de lote vive DEBAJO, en el pie.
          flex: 1,
          minHeight: 0,
          overflow: 'hidden'
        }}
      >
        {/* S239 (ENMIENDA 06): el distintivo PERMANENTE de cada lote, que llevan TODOS sus miembros
            estén o no seleccionados -- se deriva del `batchId` que cada job activo ya publica (F3),
            sin ninguna petición adicional, y su tooltip dice la MISMA etiqueta que la barra. */}
        <ActiveJobList
          items={activeItems}
          selectedFabricationOrder={fabricationOrder}
          selectedJobReference={jobReference}
          progressByKey={listProgress}
          onSelectNode={selectNode}
          batchBadges={buildBatchBadges(batchGroups, t_)}
        />

        <Box
          sx={{
            pl: { md: 1 },
            pt: { xs: 1, md: 0 },
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'center'
          }}
        >
          {fabricationOrder && jobReference
            ? (
              <ActiveSingleJob
                jobTitle={`${fabricationOrder.fabricationOrder} · ${jobReference.code || ''}`}
                jobDescription={`${fabricationOrder.partReference?.code || ''} · ${node?.name || ''}`}
                jobReference={jobReference}
                partReference={fabricationOrder?.partReference}
                node={node}
                progress={plannedEffective ? Math.round(jobProgress?.percentage ?? 0) : 0}
                target={plannedEffective || 0}
                completed={jobProgress?.done || 0}
                nok={jobProgress?.fail || 0}
                finishDisabled={finishDisabled}
                pauseDisabled={pauseDisabled}
                modeActions={modeActions}
                inPreparation={inPreparation}
                onGoToExecution={goToExecution}
                onChangeStatus={() => setModeChangeOpen(true)}
                modeChangeOpen={modeChangeOpen}
                governedByAcceptanceTooltip={governedByAcceptanceTooltip}
                reportDisabled={reportDisabled}
                reportDisabledTooltip={reportDisabledTooltip}
                materials={materials}
                onPause={() => handleChangeStatus('INTERRUPTED')}
                onFinish={handleFinishClick}
                onReportOk={handleReportOk}
                onReportNok={handleReportNok}
                counter={counter}
                setCounter={handleSetCounter}
                handleIncrement={handleIncrement}
                handleDecrement={handleDecrement}
                showPartActions={showPartActions}
                onCancelParts={() => setCancelPartsDialog({ open: true })}
                onAddParts={() => setAddPartsDialog({ open: true })}
              />
              )
            : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                <DefaultBeforeSelectText text={t_('Please select a job from the list')} />
              </Box>
              )}
        </Box>
      </Box>
      {/* 🔴 S246 (ENMIENDA 09, G3-13) -- LA PAREJA VIVE EN EL PIE, fuera del área de trabajo del job
          y con peso secundario: *"no me gusta dónde se renderizan, creo que se puede poner en otro
          sitio para que sea más user friendly"*. Sigue siendo UNA sola pareja, la del lote del JOB
          SELECCIONADO (S144/S145), con EXACTAMENTE dos acciones (S149/S207), y ahora con la etiqueta
          y el distintivo de ese lote al lado — sin ellos, una pareja en el pie no dice DE QUÉ lote es
          (OBS-N17: con seis colores dos lotes pueden coincidir). */}
      <BatchActionsBar
        group={selectedBatchGroup}
        badge={selectedBatchGroup ? buildBatchBadges(batchGroups, t_)[selectedBatchGroup.batchId] : null}
        onPauseBatch={pauseBatchGroup}
        onOpenFinishBatch={openFinishBatch}
        t_={t_}
      />
    </Box>
  )
}

export default ActiveMultiJobContainer
